import { useCallback, useMemo } from "react";
import { type ListRenderItemInfo } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import Animated from "react-native-reanimated";
import { View, Text, Button } from "@visioni/a3s-ui/components";
import { CompilationRow, CompilationRowSkeleton, type CompilationRowStatus } from "../CompilationRow";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompilationListItem {
  readonly id: string;
  readonly status: CompilationRowStatus;
  readonly cameraLabel: string;
  readonly requestedAt: string;
  readonly rangeStart: string;
  readonly rangeEnd: string;
  /**
   * Set when a `queued` job has sat long enough that it probably never
   * reached the edge. Surfaced as a warning on the row rather than as a
   * `failed` status, so the row still matches the server-side status filter
   * that returned it.
   */
  readonly staleQueued?: boolean | undefined;
  /**
   * Why the compilation failed, already turned into prose — only present when
   * `status` is `"failed"` and the edge reported a reason.
   */
  readonly failureReason?: string | undefined;
  /** Signed streaming URL for in-app playback — only present when `status` is `"ready"`. */
  readonly videoUrl?: string | undefined;
  /** Signed download URL (forces a save-as, unlike `videoUrl`) — only present when `status` is `"ready"`. */
  readonly downloadUrl?: string | undefined;
}

export interface CompilationListProps {
  readonly items: readonly CompilationListItem[];
  readonly isLoading?: boolean | undefined;
  readonly error?: boolean | undefined;
  readonly totalCount?: number | undefined;
  readonly hasNextPage?: boolean | undefined;
  readonly isFetchingNextPage?: boolean | undefined;
  readonly onLoadMore?: (() => void) | undefined;
  readonly onWatch?: ((item: CompilationListItem) => void) | undefined;
  readonly onDownload?: ((item: CompilationListItem) => void) | undefined;
}

// ---------------------------------------------------------------------------
// CompilationList
// ---------------------------------------------------------------------------

export function CompilationList({
  items,
  isLoading,
  error,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onWatch,
  onDownload,
}: CompilationListProps) {
  const { theme } = useUnistyles();

  const keyExtractor = useCallback((item: CompilationListItem) => item.id, []);

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<CompilationListItem>) => (
      <CompilationRow
        status={item.status}
        staleQueued={item.staleQueued}
        failureReason={item.failureReason}
        cameraLabel={item.cameraLabel}
        requestedAt={item.requestedAt}
        rangeStart={item.rangeStart}
        rangeEnd={item.rangeEnd}
        {...(onWatch && item.videoUrl ? { onWatch: () => onWatch(item) } : {})}
        {...(onDownload && item.downloadUrl ? { onDownload: () => onDownload(item) } : {})}
      />
    ),
    [onWatch, onDownload],
  );

  const listFooterComponent = useMemo(() => {
    const showCount = totalCount != null && items.length > 0;
    const showLoadMore = hasNextPage && onLoadMore;

    if (!showCount && !showLoadMore) return null;

    return (
      <View alignItems="center" py="4xl" gap="lg">
        {showCount && (
          <Text textStyle="xs/regular" color="textMuted">
            Showing {items.length} of {totalCount} results
          </Text>
        )}
        {showLoadMore && (
          <Button
            label="Load more"
            variant="secondary"
            variantColor="grey"
            size="sm"
            onPress={onLoadMore}
            loading={isFetchingNextPage ?? false}
            loadingText="Loading…"
          />
        )}
      </View>
    );
  }, [totalCount, items.length, hasNextPage, onLoadMore, isFetchingNextPage]);

  if (isLoading && items.length === 0) {
    return (
      <View style={styles.skeletonList}>
        <CompilationRowSkeleton />
        <CompilationRowSkeleton />
        <CompilationRowSkeleton />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centeredState}>
        <Text textStyle="sm/medium" color="danger">
          Unable to load compilations. Please try again later.
        </Text>
      </View>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <View style={styles.centeredState}>
        <Text textStyle="sm/regular" color="textMuted">
          No compilations found
        </Text>
      </View>
    );
  }

  return (
    <Animated.FlatList
      data={items as CompilationListItem[]}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      ListFooterComponent={listFooterComponent}
      ItemSeparatorComponent={Separator}
      removeClippedSubviews={false}
      style={{ flex: 1 }}
      contentContainerStyle={{
        padding: theme.spacing.xl,
      }}
      showsVerticalScrollIndicator
    />
  );
}

CompilationList.displayName = "CompilationList";

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

function Separator() {
  const { theme } = useUnistyles();
  return <View style={{ height: theme.spacing.md }} />;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  centeredState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing["4xl"],
  },
  skeletonList: {
    padding: theme.spacing.xl,
    gap: theme.spacing.md,
  },
}));
